import React, { useState } from 'react';
import {
  Badge, Box, Button, HStack, Icon, Input, Spinner, Stack, Text,
} from '@chakra-ui/react';
import { FiHash, FiLock } from 'react-icons/fi';
import useSlackChannels from '../hooks/useSlackChannels';

// item 403c — pick an EXISTING Slack channel (no free-typing). Collapsed by default: shows
// the currently-linked channel; the search + list only appear when you click "Change" (or
// when nothing is linked yet). item 379 — privateOnly lists PRIVATE channels the bot is a
// member of (the accountant thread); otherwise public channels only (server-enforced). To
// link a brand-new channel, create it in Slack first (and invite @basix if private).
const SlackChannelSelect: React.FC<{
  value: string;
  onChange: (name: string) => void;
  initialFilter?: string;
  privateOnly?: boolean;
}> = ({
  value, onChange, initialFilter, privateOnly,
}) => {
  const { data, isLoading } = useSlackChannels(privateOnly);
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState(initialFilter ?? '');
  const HashOrLock = privateOnly ? FiLock : FiHash;

  if (isLoading) {
    return (
      <HStack fontSize="xs" opacity={0.8}>
        <Spinner size="xs" />
        <Text>Loading channels…</Text>
      </HStack>
    );
  }
  if (!data?.configured) {
    return (
      <Text fontSize="xs" color="orange.400">
        Slack isn’t connected yet — channels can’t be listed.
      </Text>
    );
  }

  const channels = data.channels ?? [];

  // collapsed: a channel is linked and we're not actively changing it
  if (value && !editing) {
    const current = channels.find((c) => c.name === value);
    return (
      <HStack
        borderWidth="1px"
        borderRadius="md"
        px={2}
        py={1}
        justify="space-between"
      >
        <HStack spacing={1} minW={0}>
          <Icon as={HashOrLock} />
          <Text isTruncated fontSize="sm">{value}</Text>
          {current?.isMember && <Badge colorScheme="green" fontSize="0.5rem">bot in</Badge>}
        </HStack>
        <Button
          size="xs"
          variant="ghost"
          onClick={() => { setFilter(''); setEditing(true); }}
        >
          Change
        </Button>
      </HStack>
    );
  }

  if (channels.length === 0) {
    return (
      <Text fontSize="xs" opacity={0.7}>
        {privateOnly
          ? 'No private channels the bot can see — create one in Slack and invite @basix.'
          : 'No public channels found in the workspace.'}
      </Text>
    );
  }

  const q = filter.trim().toLowerCase();
  const shown = q ? channels.filter((c) => c.name.toLowerCase().includes(q)) : channels;

  return (
    <Stack spacing={1}>
      <Input
        size="sm"
        placeholder="Search public channels…"
        value={filter}
        autoFocus
        onChange={(e) => setFilter(e.target.value)}
      />
      <Box maxH="170px" overflowY="auto" borderWidth="1px" borderRadius="md" p={1}>
        {shown.map((c) => (
          <Button
            key={c.id}
            size="xs"
            variant={c.name === value ? 'solid' : 'ghost'}
            colorScheme={c.name === value ? 'purple' : undefined}
            justifyContent="flex-start"
            w="full"
            leftIcon={<HashOrLock />}
            onClick={() => { onChange(c.name); setEditing(false); }}
          >
            <HStack w="full" justify="space-between" spacing={2}>
              <Text isTruncated>{c.name}</Text>
              {c.isMember && <Badge colorScheme="green" fontSize="0.5rem">bot in</Badge>}
            </HStack>
          </Button>
        ))}
        {shown.length === 0 && (
          <Text fontSize="xs" p={2} opacity={0.7}>{`No channels match “${filter}”.`}</Text>
        )}
      </Box>
    </Stack>
  );
};

SlackChannelSelect.defaultProps = { initialFilter: undefined, privateOnly: false };

export default SlackChannelSelect;
